<template>
  <div class="home">
    <HubHero />

    <main>
      <section class="ventures-section" id="portfolio">
        <div class="container">
          <div class="section-title text-center">
            <span class="subtitle">Leistungsbereiche</span>
            <h2>Welcher <span class="text-gradient">Bereich passt zu Ihrem Thema</span>?</h2>
            <p class="lead">
              Jeder Bereich hat einen eigenen Schwerpunkt. So können Sie schnell einordnen, ob es eher um Prozesse, Produktaufbau, KI, medizinische Themen oder ein konkretes Produkt geht.
            </p>
          </div>

          <VentureGrid :ventures="ventures" />
        </div>
      </section>

      <section class="devops-section" id="devops">
        <div class="container">
          <div class="section-title text-center">
            <span class="subtitle">Kernleistungen</span>
            <h2>DevOps & Projektmanagement als <span class="text-gradient">Basis für saubere Umsetzung</span></h2>
            <p class="lead">
              Viele Vorhaben scheitern nicht an der Idee, sondern daran, dass Umsetzung und Betrieb nicht sauber aufgesetzt sind. DevOps und Projektmanagement sorgen dafür, dass Projekte überschaubar bleiben und zuverlässig laufen.
            </p>
          </div>

          <div class="devops-grid">
            <article class="devops-card">
              <span class="devops-step">DevOps</span>
              <h3>Technische Delivery & Betriebsfähigkeit</h3>
              <p>
                Für Unternehmen, die Releases stabiler machen, Infrastruktur aufräumen oder den laufenden Betrieb besser in den Griff bekommen wollen.
              </p>
              <ul>
                <li>Aufbau und Optimierung von Deployment-Pipelines</li>
                <li>Infrastruktur-Audit und Migration zu IaC</li>
                <li>Monitoring, Observability und Betriebskonzepte</li>
                <li>Cloud-Architektur (AWS, GCP) und Kostenoptimierung</li>
              </ul>
            </article>

            <article class="devops-card">
              <span class="devops-step">Projektmanagement</span>
              <h3>Steuerung, Tooling & Working-Setups</h3>
              <p>
                Für Teams, die Projekte klarer steuern, sinnvollere Tools einsetzen und im Alltag strukturierter zusammenarbeiten möchten.
              </p>
              <ul>
                <li>Einführung und Optimierung agiler Prozesse (Scrum, Kanban)</li>
                <li>Tool-Auswahl und -Konfiguration (Jira, Confluence, Plane)</li>
                <li>Projektsteuerung, Reporting und Risikomanagement</li>
                <li>Team-Coaching und Working-Setup-Beratung</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section class="model-section" id="model">
        <div class="container">
          <div class="section-title text-center">
            <span class="subtitle">Überblick</span>
            <h2>Von <span class="text-gradient">Beratung bis Produkt</span> klar aufgeteilt</h2>
            <p class="lead">
              Nicht jedes Thema braucht dieselbe Art von Unterstützung. Manche Bereiche sind klar beratungsnah, andere gehen direkt in die Umsetzung und wieder andere sind eigenständige Produkte.
            </p>
          </div>

          <div class="model-grid">
            <article class="model-card">
              <h3>JT Innovation</h3>
              <p>Wenn Abläufe zu aufwendig sind, zu viel Handarbeit drinsteckt oder Digitalisierung im Alltag endlich spürbar werden soll.</p>
            </article>

            <article class="model-card">
              <h3>JT MVP Studio</h3>
              <p>Wenn aus einer Idee oder Anforderung zügig ein MVP, Prototyp oder ein sinnvoller nächster Release-Schritt werden soll.</p>
            </article>

            <article class="model-card">
              <h3>JT AI Advisory</h3>
              <p>Wenn KI kein Buzzword bleiben soll, sondern sinnvoll priorisiert und in echte Anwendungsfälle übersetzt werden muss.</p>
            </article>

            <article class="model-card">
              <h3>JT Medical Consulting</h3>
              <p>Wenn medizinische Abläufe, Digitalisierung und IT-Strukturen sauber zusammenpassen müssen.</p>
            </article>

            <article class="model-card">
              <h3>JT Focus und manaforgeai</h3>
              <p>Zwei eigene Produkte: JT Focus für self-hosted Projektmanagement und manaforgeai für AI-native Systeme und Workflows.</p>
            </article>

            <article class="model-card">
              <h3>DevOps und Projektmanagement</h3>
              <p>Hier liegt die Grundlage dafür, dass Projekte nicht nur starten, sondern auch sauber umgesetzt und stabil betrieben werden.</p>
            </article>
          </div>
        </div>
      </section>

      <section class="origin-section" id="origin">
        <div class="container">
          <div class="section-title text-center">
            <span class="subtitle">Erfahrung</span>
            <h2>Die Grundlage liegt in <span class="text-gradient">DevOps, Delivery und Projektsteuerung</span></h2>
            <p class="lead">
              Die heutigen Bereiche sind nicht am Reißbrett entstanden, sondern aus echter Projektarbeit, laufendem Betrieb und konkreten Problemen im Alltag.
            </p>
          </div>

          <div class="origin-timeline">
            <div class="timeline-item">
              <span class="timeline-step">01</span>
              <div class="timeline-body">
                <h3>DevOps und technische Delivery</h3>
                <p>Der Ausgangspunkt war die Verantwortung für Deployments, Infrastruktur, Automatisierung und stabile technische Abläufe.</p>
              </div>
            </div>
            <div class="timeline-item">
              <span class="timeline-step">02</span>
              <div class="timeline-body">
                <h3>Projektmanagement und Tool-Realität</h3>
                <p>Dazu kam die praktische Arbeit an Projektsteuerung, Teamabläufen und Tools, die im Alltag wirklich helfen.</p>
              </div>
            </div>
            <div class="timeline-item">
              <span class="timeline-step">03</span>
              <div class="timeline-body">
                <h3>Heute: klar gegliederte Bereiche</h3>
                <p>Aus dieser Arbeit sind mit der Zeit eigene Bereiche und Produkte entstanden.</p>
              </div>
            </div>
          </div>

          <div class="origin-grid">
            <article class="origin-card">
                <h3>DevOps als Basis</h3>
                <p>
                  Ohne stabile technische Basis bringen neue Ideen wenig. Deshalb ist DevOps hier kein Nebenthema.
                </p>
            </article>

            <article class="origin-card">
                <h3>Projektmanagement als Praxisfeld</h3>
                <p>
                  Aus echter Projektpraxis sind nicht nur Leistungen, sondern auch eigene Produkte wie JT Focus entstanden.
                </p>
            </article>

            <article class="origin-card">
                <h3>Von der Praxis zu klaren Angeboten</h3>
                <p>
                  Viele Themen kamen direkt aus Projekten und wurden später zu eigenständigen Angeboten weiterentwickelt.
                </p>
            </article>
          </div>
        </div>
      </section>

      <FounderSection />
      <HubContactCta />
    </main>
  </div>
</template>

<script>
import HubHero from '@/components/HubHero.vue';
import VentureGrid from '@/components/VentureGrid.vue';
import FounderSection from '@/components/FounderSection.vue';
import HubContactCta from '@/components/HubContactCta.vue';
import { ventures } from '@/data/ventures';

export default {
  name: 'AppHome',
  components: {
    FounderSection,
    HubContactCta,
    HubHero,
    VentureGrid
  },
  data() {
    return {
      ventures
    };
  }
};
</script>

<style lang="scss" scoped>
@use "@/assets/css/global.scss" as *;

.ventures-section {
  background: linear-gradient(180deg, #f8fafc, #eef3f7);
}

.devops-section {
  background: linear-gradient(180deg, rgba($accent-color, 0.03), rgba($accent-teal, 0.03));
}

.model-section {
  background: linear-gradient(180deg, rgba($accent-purple, 0.04), rgba($accent-color, 0.03));
}

.origin-section {
  background: linear-gradient(180deg, #ffffff, rgba($accent-teal, 0.02));
}

.devops-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}

.devops-card {
  padding: 2rem;
  border-radius: 22px;
  background: $surface-color;
  border: 1px solid $line-color;
  box-shadow: $shadow-md;
}

.devops-step {
  display: inline-block;
  margin-bottom: 1rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background: $primary-gradient;
  color: #07111f;
}

.devops-card h3 {
  margin-bottom: 0.85rem;
}

.devops-card p:last-child {
  margin-bottom: 0;
}

.devops-card ul {
  margin-top: 1rem;
  padding-left: 1.2rem;
}

.devops-card li + li {
  margin-top: 0.5rem;
}

.model-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
}

.origin-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
}

.origin-timeline {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
}

.origin-timeline::before {
  content: '';
  position: absolute;
  top: 28px;
  left: 12%;
  right: 12%;
  height: 1px;
  background: linear-gradient(90deg, rgba($accent-teal, 0.2), rgba($accent-purple, 0.35), rgba($accent-teal, 0.15));
}

.timeline-item {
  position: relative;
  padding-top: 0.25rem;
}

.timeline-step {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 1rem;
  border-radius: 999px;
  background: $primary-gradient;
  color: #fff;
  font-weight: 800;
  box-shadow: $shadow-md;
}

.timeline-body {
  padding: 1.5rem;
  border-radius: 22px;
  background: $surface-color;
  border: 1px solid $line-color;
  box-shadow: $shadow-md;
}

.timeline-body h3 {
  margin-bottom: 0.75rem;
}

.timeline-body p:last-child {
  margin-bottom: 0;
}

.model-card {
  padding: 1.75rem;
  border-radius: 22px;
  background: $surface-color;
  border: 1px solid $line-color;
  box-shadow: $shadow-md;
}

.origin-card {
  padding: 1.75rem;
  border-radius: 22px;
  background: $surface-color;
  border: 1px solid $line-color;
  box-shadow: $shadow-md;
}

.model-card h3 {
  margin-bottom: 0.85rem;
}

.origin-card h3 {
  margin-bottom: 0.85rem;
}

.model-card p:last-child {
  margin-bottom: 0;
}

.origin-card p:last-child {
  margin-bottom: 0;
}

@media (max-width: 991px) {
  .devops-grid,
  .model-grid,
  .origin-timeline,
  .origin-grid {
    grid-template-columns: 1fr;
  }

  .origin-timeline::before {
    display: none;
  }
}
</style>
